"use client"

import { notify } from "@/lib/notifications/notify"
import { useEffect, useRef, useState } from "react"
import { Loader2, Send } from "lucide-react"
import { toast } from "sonner"
import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"

// Modal that handles a barber's "I'm interested" inquiry on a job listing.
// Mirrors the owner-side InquireModal pattern:
//   1. notification → owner   (type: 'job_inquiry')
//   2. get-or-create conversation between barber + owner
//   3. message inserted into the existing messages table
//   4. job_inquiries row inserted (audit trail; status starts 'pending')
// A synchronous ref guard prevents rapid-click double-submits.

interface JobInquireModalProps {
  open: boolean
  onClose: () => void
  listingId: string
  listingTitle: string
  shopId: string
  shopName: string
  ownerId: string
  barberId: string
  barberName: string
  onSent?: () => void
}

function defaultMessage(barberName: string, shopName: string, listingTitle: string) {
  return `Hey ${shopName}, I came across your '${listingTitle}' post and I'm interested. Would you be open to chatting? — ${barberName}`
}

export function JobInquireModal({
  open, onClose, listingId, listingTitle, shopId, shopName, ownerId, barberId, barberName, onSent,
}: JobInquireModalProps) {
  const supabase = createClient()
  const [message, setMessage] = useState("")
  const [sending, setSending] = useState(false)
  const sendingRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setMessage(defaultMessage(barberName, shopName, listingTitle))
  }, [open, barberName, shopName, listingTitle])

  const send = async () => {
    if (sendingRef.current) return
    if (!message.trim()) { toast.error("Add a message"); return }
    sendingRef.current = true
    setSending(true)
    try {
      // 1. Notification to the owner
      const { error: notifErr } = await notify({
        recipient: ownerId,
        type: "job_inquiry",
        jobId: listingId,
        shopId,
        metadata: { listing_id: listingId, shop_id: shopId, barber_id: barberId },
      })
      if (notifErr) throw notifErr

      // 2. Get-or-create conversation between barber and owner
      const { data: convo, error: convoSelErr } = await supabase
        .from("conversations")
        .select("id")
        .or(`and(participant_1.eq.${barberId},participant_2.eq.${ownerId}),and(participant_1.eq.${ownerId},participant_2.eq.${barberId})`)
        .maybeSingle()
      if (convoSelErr) throw convoSelErr

      let conversationId = convo?.id
      if (!conversationId) {
        const { data: newConvo, error: newConvoErr } = await supabase
          .from("conversations")
          .insert({ participant_1: barberId, participant_2: ownerId })
          .select("id")
          .single()
        if (newConvoErr) throw newConvoErr
        conversationId = newConvo?.id
      }

      // 3. Message body
      if (conversationId) {
        const { error: msgErr } = await supabase.from("messages").insert({
          conversation_id: conversationId,
          sender_id: barberId,
          receiver_id: ownerId,
          content: message.trim(),
        })
        if (msgErr) throw msgErr
      }

      // 4. job_inquiries audit row (idempotent — UNIQUE(listing_id, barber_id))
      const { error: inqErr } = await supabase.from("job_inquiries").insert({
        listing_id: listingId,
        shop_id: shopId,
        owner_id: ownerId,
        barber_id: barberId,
        message: message.trim(),
        conversation_id: conversationId,
      })
      // 23505 = unique violation. If they already inquired, the message
      // still went through above so we don't surface this as an error.
      if (inqErr && inqErr.code !== "23505") throw inqErr

      toast.success("Inquiry sent")
      onSent?.()
      onClose()
    } catch (err: any) {
      toast.error(err?.message || "Couldn't send inquiry")
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-lg bg-[#111111] border-white/10 text-white rounded-3xl p-0 gap-0 [&>button]:text-gray-400 [&>button]:hover:text-white [&>button]:opacity-100 [&>button]:top-5 [&>button]:right-5">
        <div className="flex justify-center pt-6 pb-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://cdn.prod.website-files.com/67ae3561fb0d370bd1252062/67ae3d469bdbe28e3204ee5d_booth_logo_white%20(1).svg"
            alt="BOOTH"
            width={72}
            height={24}
            className="h-6 w-auto opacity-95"
          />
        </div>
        <DialogHeader className="px-6 pt-2 pb-2 space-y-2">
          <DialogTitle className="text-white text-lg font-semibold leading-snug pr-8">
            Inquire about this position
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-sm leading-relaxed pb-1">
            {listingTitle} · {shopName}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-5">
          <div className="space-y-2.5">
            <label htmlFor="job-inquire-message" className="block text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Your message
            </label>
            <Textarea
              id="job-inquire-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={1000}
              className="resize-none bg-[#0a0a0a] border-white/15 text-white placeholder:text-gray-500 text-sm rounded-2xl focus-visible:ring-1 focus-visible:ring-white/20 focus-visible:border-white/30"
              placeholder="A short note about your experience and why you're interested…"
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-white/10 sm:gap-2">
          <Button variant="ghost" onClick={onClose} disabled={sending} className="text-gray-300 hover:bg-white/5 hover:text-white rounded-full">
            Cancel
          </Button>
          <Button onClick={send} disabled={sending || !message.trim()} className="gap-1.5 bg-white text-black hover:bg-gray-200 rounded-full">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send inquiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
